import { parse, parseFragment } from "parse5";
import { fail, LIMITS } from "./types";
export type HNode = {
  tag: string;
  attrs: Map<string, string>;
  children: HNode[];
  parent?: HNode;
  text: string;
  start: number;
  end: number;
};
export const attr = (n: HNode, key: string) => n.attrs.get(key.toLowerCase());
export function htmlTree(html: string, fragment = false): HNode {
  if (new TextEncoder().encode(html).length > LIMITS.fileBytes)
    fail("invalide", "Taille HTML supérieure à 2 Mo.");
  const raw: any = fragment
    ? parseFragment(html, { sourceCodeLocationInfo: true })
    : parse(html, { sourceCodeLocationInfo: true });
  const root: HNode = {
    tag: "#root",
    attrs: new Map(),
    children: [],
    text: "",
    start: 0,
    end: html.length,
  };
  const queue: { raw: any; out: HNode; depth: number }[] = [
    { raw, out: root, depth: 0 },
  ];
  let count = 0;
  while (queue.length) {
    const { raw, out, depth } = queue.pop()!;
    if (++count > LIMITS.nodes || depth > 200)
      fail("invalide", "Limite de nœuds ou profondeur HTML dépassée.");
    for (const ch of raw.childNodes || []) {
      const n: HNode = {
        tag: ch.tagName || ch.nodeName,
        attrs: new Map((ch.attrs || []).map((a: any) => [a.name, a.value])),
        children: [],
        parent: out,
        text: ch.value || "",
        start: ch.sourceCodeLocation?.startOffset || 0,
        end: ch.sourceCodeLocation?.endOffset || 0,
      };
      out.children.push(n);
      if (ch.tagName !== "template")
        queue.push({ raw: ch, out: n, depth: depth + 1 });
    }
  }
  return root;
}
export function descendants(n: HNode): HNode[] {
  const result: HNode[] = [];
  const q = [...n.children].reverse();
  while (q.length) {
    const c = q.pop()!;
    result.push(c);
    q.push(...c.children.slice().reverse());
  }
  return result;
}
export function textOf(n: HNode): string {
  const block = new Set([
    "p",
    "div",
    "li",
    "ul",
    "ol",
    "section",
    "article",
    "h1",
    "h2",
    "h3",
    "h4",
    "blockquote",
    "br",
    "hr",
    "dt",
    "dd",
  ]);
  const out: string[] = [];
  const walk = (x: HNode) => {
    if (["script", "style", "template"].includes(x.tag)) return;
    if (block.has(x.tag)) out.push(" ");
    if (x.tag === "#text") out.push(x.text);
    for (const child of x.children) walk(child);
    if (block.has(x.tag)) out.push(" ");
  };
  walk(n);
  return out.join("");
}
export function hasAncestor(n: HNode, tags: string[]) {
  let p = n.parent;
  while (p) {
    if (tags.includes(p.tag)) return true;
    p = p.parent;
  }
  return false;
}
export const space = (s: string) =>
  s.normalize("NFC").replace(/\s+/gu, " ").trim();
export const presented = (s: string) => space(textOf(htmlTree(s, true)));
type Part = { simple: string; relation: "root" | "descendant" | "child" };
export function parseSelector(s: string): Part[] {
  if (!s || s.length > 400) fail("invalide", "Sélecteur vide ou trop long.");
  const parts: Part[] = [];
  let i = 0,
    relation: Part["relation"] = "root";
  const token =
    /^(?:[a-zA-Z][\w-]*|\*)?(?:(?:[.#][a-zA-Z_][\w-]*)|(?:\[[a-zA-Z_][\w-]*(?:=(?:"[^"\]\r\n]*"|'[^'\]\r\n]*'))?\]))*/;
  while (i < s.length) {
    const m = s.slice(i).match(token)?.[0] || "";
    if (!m) fail("invalide", `Sélecteur non pris en charge : ${s}`);
    parts.push({ simple: m, relation });
    i += m.length;
    if (i === s.length) break;
    const ws = s.slice(i).match(/^\s+/)?.[0] || "";
    i += ws.length;
    if (s[i] === ">") {
      relation = "child";
      i++;
      i += (s.slice(i).match(/^\s+/)?.[0] || "").length;
    } else if (ws) {
      relation = "descendant";
      if (i === s.length) break;
    } else fail("invalide", `Sélecteur non pris en charge : ${s}`);
    if (i === s.length) fail("invalide", "Sélecteur incomplet.");
  }
  return parts;
}
function simpleMatch(n: HNode, s: string) {
  let rest = s;
  const tag = rest.match(/^(?:[a-zA-Z][\w-]*|\*)/)?.[0];
  if (tag) {
    if (tag !== "*" && n.tag !== tag.toLowerCase()) return false;
    rest = rest.slice(tag.length);
  } else if (n.tag.startsWith("#")) return false;
  const tokens =
    rest.match(
      /[.#][a-zA-Z_][\w-]*|\[[a-zA-Z_][\w-]*(?:=(?:"[^"\]]*"|'[^'\]]*'))?\]/g,
    ) || [];
  return tokens.every((t) => {
    if (t[0] === "#") return attr(n, "id") === t.slice(1);
    if (t[0] === ".")
      return (attr(n, "class") || "").split(/\s+/).includes(t.slice(1));
    const m = t.match(/^\[([\w-]+)(?:=["'](.*)["'])?\]$/)!;
    return m[2] === undefined
      ? n.attrs.has(m[1].toLowerCase())
      : attr(n, m[1]) === m[2];
  });
}
export function select(root: HNode, selector: string): HNode[] {
  const ps = parseSelector(selector);
  const match = (n: HNode, i: number): boolean => {
    if (!simpleMatch(n, ps[i].simple)) return false;
    if (i === 0) return true;
    if (ps[i].relation === "child")
      return !!n.parent && n.parent !== root && match(n.parent, i - 1);
    let p = n.parent;
    while (p && p !== root) {
      if (match(p, i - 1)) return true;
      p = p.parent;
    }
    return false;
  };
  return descendants(root).filter((n) => match(n, ps.length - 1));
}
export function subOne(n: HNode, sel: string | undefined): HNode {
  if (!sel) return n;
  const values = select(n, sel);
  if (values.length !== 1)
    fail(
      "non_determine",
      `${values.length} éléments pour le sous-sélecteur ${sel} ; une valeur attendue.`,
    );
  return values[0];
}
export function activeScripts(root: HNode) {
  return descendants(root).filter(
    (n) =>
      n.tag === "script" &&
      (attr(n, "type") || "").trim().toLowerCase() === "application/ld+json" &&
      !hasAncestor(n, ["pre", "code", "template"]),
  );
}
export const scriptText = (n: HNode) => n.children.map((x) => x.text).join("");
